import {
  createContext,
  ReactNode,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
} from 'react';

import {useNavigate} from 'react-router';

import {ECardThemesIds} from '@/dataSource/src/api/cards-theme/entities';

import {useTrackers} from './trackers';

export const ITEMS_PER_SLIDE_DESKTOP = 3;
export const ITEMS_PER_SLIDE_MOBILE = 2;

type ChooseCardThemeCategoryViewModelContextType = {
  isConfirmCardThemeModalOpen: boolean;
  cardThemeSelected: ECardThemesIds;
  onConfirmCardThemeModal: () => void;
  onCardThemeSelected: (cardTheme: string) => void;
  onCloseCardThemeModal: () => void;
};

const ChooseCardThemeCategoryViewModelContext = createContext<
  ChooseCardThemeCategoryViewModelContextType | undefined
>(undefined);

export function ChooseCardThemeCategoryViewModelProvider({
  children,
}: {
  children: ReactNode;
}) {
  //#region [---- [TRACK EVENTS] ----]
  const {
    trackSelectOnboardingChooseCardTheme,
    trackViewOnboardingSelectCardTheme,
    trackViewOnboardingModalPreviewCardTheme,
  } = useTrackers();
  //#endregion

  //#region [---- [HOOKS] ----]
  const navigate = useNavigate();
  const [isConfirmCardThemeModalOpen, setIsConfirmCardThemeModalOpen] =
    useState<boolean>(false);
  const cardThemeSelected = useRef<ECardThemesIds>(undefined);

  useEffect(() => {
    trackViewOnboardingSelectCardTheme();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  //#endregion

  //#region  [---- [LOGIC] ----]
  const onCloseCardThemeModal = useCallback(() => {
    setIsConfirmCardThemeModalOpen(false);
  }, []);

  const onConfirmCardThemeModal = useCallback(() => {
    trackSelectOnboardingChooseCardTheme(cardThemeSelected.current);
    setIsConfirmCardThemeModalOpen(false);
    navigate('/onboarding/preload-card-data', {
      state: {
        selectedTheme: cardThemeSelected.current,
      },
    });
  }, [navigate, trackSelectOnboardingChooseCardTheme]);

  const onCardThemeSelected = useCallback(
    (cardTheme: ECardThemesIds) => {
      cardThemeSelected.current = cardTheme;
      setIsConfirmCardThemeModalOpen(true);
      trackViewOnboardingModalPreviewCardTheme(cardTheme);
    },
    [trackViewOnboardingModalPreviewCardTheme],
  );
  //#endregion

  return (
    <ChooseCardThemeCategoryViewModelContext.Provider
      value={{
        isConfirmCardThemeModalOpen,
        cardThemeSelected: cardThemeSelected.current,
        onConfirmCardThemeModal,
        onCardThemeSelected,
        onCloseCardThemeModal,
      }}>
      {children}
    </ChooseCardThemeCategoryViewModelContext.Provider>
  );
}

export function useChooseCardThemeCategoryProvider() {
  const context = useContext(ChooseCardThemeCategoryViewModelContext);
  if (context === undefined) {
    throw new Error('ChooseCardThemeCategory ViewModelContext is undefined');
  }
  return context;
}
